import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_ID = "jrs-contact-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db701uucdv04m4p4ge7g";

const contactInfo = [
  {
    icon: "ri-mail-line",
    label: "邮箱地址",
    value: "support@jrs-live.demo",
  },
  {
    icon: "ri-customer-service-2-line",
    label: "合作咨询",
    value: "business@jrs-live.demo",
  },
  {
    icon: "ri-time-line",
    label: "服务时间",
    value: "全天候在线，工作日 2 小时内回复",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [formError, setFormError] = useState("");

  usePageMeta(
    "联系我们 - jrs直播",
    "联系 jrs直播团队，提交赛事合作、内容建议或使用反馈。填写联系表单或通过邮箱与我们取得联系，我们期待与你交流。",
    "联系我们,jrs直播联系方式,赛事合作,内容反馈,体育直播合作",
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSuccess = response.ok && parsed?.code === "OK";

      if (isSuccess) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          String(serverMsg) || "提交失败，请检查填写内容后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
      <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">联系我们</span>
      </nav>

      <h1 className="mt-5 font-heading text-3xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
        联系我们
      </h1>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
        无论是赛事合作、内容建议还是使用反馈，都欢迎与我们联系。填写下方表单或通过邮箱地址与我们取得联系。
      </p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.2fr]">
        <div className="flex flex-col gap-4">
          {contactInfo.map((item) => (
            <div
              key={item.label}
              className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                <i className={`${item.icon} text-xl`} />
              </span>
              <div>
                <div className="text-xs text-foreground-500">{item.label}</div>
                <div className="mt-1 text-sm font-medium text-foreground-900">
                  {item.value}
                </div>
              </div>
            </div>
          ))}
          <div className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-bold text-foreground-950">
              关注我们
            </h2>
            <p className="mt-1 text-xs text-foreground-500">
              获取最新赛事资讯与直播提醒
            </p>
            <div className="mt-4 flex items-center gap-3">
              {["ri-wechat-line", "ri-weibo-line", "ri-bilibili-line", "ri-mail-line"].map(
                (icon) => (
                  <span
                    key={icon}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-500/50 hover:text-primary-400 cursor-pointer"
                  >
                    <i className={`${icon} text-base`} />
                  </span>
                ),
              )}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            提交你的信息
          </h2>
          <p className="mt-1 text-sm text-foreground-500">
            请留下你的联系方式与具体需求，我们会尽快回复。
          </p>

          <form
            id={FORM_ID}
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-5 flex flex-col gap-4"
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                姓名
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                placeholder="请输入你的姓名"
                className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                邮箱
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                placeholder="请输入你的邮箱地址"
                className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请描述你的需求或建议（最多 500 字）"
                className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
              />
            </div>

            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="field-honeypot"
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer"
            >
              <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
              {status === "submitting" ? "提交中..." : "提交信息"}
            </button>

            {status === "success" && (
              <p className="flex items-center gap-2 rounded-md border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-400">
                <i className="ri-check-line" />
                提交成功，感谢你的留言，我们会尽快回复。
              </p>
            )}
            {status === "error" && (
              <p className="flex items-center gap-2 rounded-md border border-accent-500/40 bg-accent-500/10 px-4 py-3 text-sm text-accent-400">
                <i className="ri-error-warning-line" />
                {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}